import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Gravatar from 'react-gravatar';
import md5 from 'blueimp-md5';

import { withTheme } from 'common/styling/theme';

import { DateCell, DefaultButton, NewPaginationTable, RowLink, Tooltip } from 'common/components';
import { InnerPartnershipUsersTable } from '..';

import { staticStyles, getDynamicStyles } from './style';

const PartnershipUsersTable = ({
  data: newData,
  history,
  getList,
  loading,
  location,
  params,
  viewEmailPermission,
  viewPhonePermission,
  viewSubIbTreePermission,
  viewClientsUserTags,
  theme,
  referType,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [data, setData] = useState(newData);
  useEffect(() => {
    setData(newData);
  }, [loading]);

  const getLink = id =>
    referType === 'refer_friend'
      ? `/referFriend/clients/user/${id}/accounts`
      : `/partnership/clients/user/${id}/accounts`;

  /* eslint-disable */
  const columns = [
    {
      accessor: 'lastName',
      Header: 'justFullName',
      isSortable: true,

      Cell: ({ rowData: { email, fullName, id } }) => (
        <RowLink isPreviewCell className="PartnershipUsersTable__user__link" link={getLink(id)}>
          <div className="PartnershipUsersTable__user">
            {viewEmailPermission && <Gravatar md5={md5(email)} size={35} />}
            <span>{fullName}</span>
          </div>
        </RowLink>
      ),
    },
    ...(viewSubIbTreePermission
      ? [
          {
            Header: 'justIBOrClient',
            accessor: 'isPartner',
            isSortable: true,
            Cell: ({ rowData: { isPartner, id } }) => (
              <RowLink isPreviewCell link={getLink(id)}>
                <span>{isPartner ? 'Sub_IB 1st level' : 'Client'}</span>
              </RowLink>
            ),
          },
        ]
      : []),
    ...(viewEmailPermission
      ? [
          {
            Header: 'justEmail',
            accessor: 'email',
            isSortable: true,
            Cell: ({ rowData: { email, id } }) => (
              <RowLink isPreviewCell link={getLink(id)}>
                <span>{email || '一'}</span>
              </RowLink>
            ),
          },
        ]
      : []),

    ...(viewPhonePermission
      ? [
          {
            Header: 'justPhone',
            accessor: 'phone',
            isSortable: true,
            Cell: ({ rowData: { phone, id } }) => (
              <RowLink isPreviewCell link={getLink(id)}>
                <span>{phone || '一'}</span>
              </RowLink>
            ),
          },
        ]
      : []),
    {
      Header: 'justDateJoined',
      accessor: 'dateJoined',
      isSortable: true,
      Cell: ({ rowData: { dateJoined, id } }) => (
        <RowLink isPreviewCell link={getLink(id)}>
          <DateCell value={dateJoined} />
        </RowLink>
      ),
    },

    ...(viewSubIbTreePermission
      ? [
          {
            Header: 'justActions',
            accessor: 'actions',
            canExpand: true,
            Cell: ({ rowData: { isPartner, id }, isExpanded }) => {
              const handleShowClientsButton = e => {
                e.stopPropagation();
                const updatedResults = data.results.reduce((acc, rec) => {
                  if (rec.id === id) {
                    if (rec.isExpanded) {
                      return [...acc, { ...rec, isExpanded: false }];
                    }
                    return [...acc, { ...rec, isExpanded: true }];
                  }
                  return [...acc, rec];
                }, []);
                setData({ ...data, results: updatedResults });
              };

              return isPartner ? (
                <DefaultButton
                  textId={isExpanded ? 'justHideClients' : 'justShowClients'}
                  type="button"
                  noBorder
                  onClick={handleShowClientsButton}
                />
              ) : null;
            },
          },
        ]
      : []),
    ...(viewClientsUserTags
      ? [
          {
            Header: 'justTags',
            isSortable: false,
            Cell: ({ rowData: { tags } }) => {
              return (
                <div className="PartnershipUsersTable__tags">
                  {tags.map((tag, index) => (
                    <Tooltip id={`UserTag__${index}`} textId={tag.description} key={index} noTranslate place="bottom">
                      <div className="UserTagsPanelClient__tag">
                        <p>{tag.name}</p>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const renderRowSubComponent = useCallback(
    ({ rowData }) => {
      const userId = rowData.id;
      return (
        <InnerPartnershipUsersTable
          colspan={columns.length}
          userId={userId}
          isShowEmail={viewEmailPermission}
          isShowPhone={viewPhonePermission}
          viewClientsUserTags={viewClientsUserTags}
          referType={referType}
        />
      );
    },
    [columns.length]
  );

  /* eslint-enable */

  const onRowClick = ({ rowData }) => {
    history.push(
      referType === 'refer_friend'
        ? `/referFriend/clients/user/${rowData.id}/accounts`
        : `/partnership/clients/user/${rowData.id}/accounts/`
    );
  };

  return (
    <div className={classNames('PartnershipUsersTable')}>
      <NewPaginationTable
        data={data}
        loading={loading}
        columns={columns}
        onRowClick={onRowClick}
        getList={getList}
        location={location}
        history={history}
        params={params}
        renderRowSubComponent={renderRowSubComponent}
        isCustomTable
        noResponsive
      />
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

PartnershipUsersTable.propTypes = {
  data: PropTypes.object.isRequired,
  viewEmailPermission: PropTypes.bool.isRequired,
  viewPhonePermission: PropTypes.bool.isRequired,
  viewSubIbTreePermission: PropTypes.bool.isRequired,
  viewClientsUserTags: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  getList: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  referType: PropTypes.string.isRequired,
};

export default withTheme()(PartnershipUsersTable);
